import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { StarIcon, QuoteIcon, ChevronLeftIcon, ChevronRightIcon, MapPinIcon } from 'lucide-react';
import { reviews } from '../data/reviews';
export function Reviews() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const review = reviews[index];
  const go = useCallback((step: number) => {
    setDirection(step);
    setIndex(current => (current + step + reviews.length) % reviews.length);
  }, []);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') go(1);
    if (event.key === 'ArrowLeft') go(-1);
  };
  return <section id="reviews" aria-label="Customer reviews" className="w-full bg-forest-800 py-16 lg:py-20" data-id="element-295" data-mp="3a1t4mca4hmh0">
      <div className="mx-auto max-w-4xl px-5" data-id="element-296" data-mp="3f1nviftwec22">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-297" data-mp="3jzh0j5jgt00r">
          <div data-id="element-298" data-mp="2rcnqtixsguox">
            <p className="font-display text-sm uppercase tracking-[0.2em] text-ember-500" data-id="element-299" data-mp="2rcdw5i1stzcw">
              What neighbors say
            </p>
            <h2 className="mt-2 font-display text-3xl uppercase leading-tight text-white sm:text-4xl" data-id="element-300" data-mp="2mcswf5et1mz4">
              Rated 5 stars across North County
            </h2>
          </div>
          <p className="text-sm text-white/70" data-id="element-301" data-mp="2ddxuw6mph125">
            {reviews.length} verified reviews from local homeowners and property managers
          </p>
        </div>

        <div className="relative mt-8 outline-none" tabIndex={0} role="group" aria-roledescription="carousel" aria-label={`Review ${index + 1} of ${reviews.length}`} onKeyDown={onKeyDown} data-id="element-302" data-mp="354c1riwaxd4b">
          <div className="min-h-[300px] rounded-lg bg-forest-900 p-7 sm:min-h-[260px] sm:p-9" data-id="element-303" data-mp="2wbi7ae8aa75v">
            <AnimatePresence mode="wait" initial={false} custom={direction} data-id="element-304" data-mp="3sv7o40hzng46">
              <motion.figure key={review.id} custom={direction} initial={{
              opacity: 0,
              x: direction * 24
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction * -24
            }} transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-305" data-mp="3o1vbkoql2zrs">
                <div className="flex items-center justify-between gap-4" data-id="element-306" data-mp="1cdqfx2xjp8fm">
                  <div className="flex items-center gap-1" aria-label={`${review.rating} out of 5 stars`} data-id="element-307" data-mp="1hcute5ri5sbr">
                    {Array.from({
                    length: review.rating
                  }).map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-ember-500 text-ember-500" aria-hidden="true" data-id="element-308" data-mp="18fkfsdr7pr3o" data-mpi={i} />)}
                  </div>
                  <QuoteIcon className="h-6 w-6 shrink-0 text-white/25" aria-hidden="true" data-id="element-309" data-mp="2nuzq0yusao49" />
                </div>

                <blockquote className="mt-5 text-lg leading-relaxed text-white sm:text-xl" data-id="element-310" data-mp="2sucka4nzixgv">
                  &ldquo;{review.quote}&rdquo;
                </blockquote>

                <figcaption className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-white/10 pt-4" data-id="element-311" data-mp="h44vvr2esywa">
                  <span className="font-display text-base uppercase tracking-wide text-white" data-id="element-312" data-mp="qwsoe9mcwgp7">
                    {review.author}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm text-white/70" data-id="element-313" data-mp="m49blevhtplg">
                    <MapPinIcon className="h-3.5 w-3.5 text-ember-500" aria-hidden="true" data-id="element-314" data-mp="zve4ly4cvjmt" />
                    {review.location}
                  </span>
                  <span className="text-sm text-white/70" data-id="element-315" data-mp="19uwokek9n5ev">{review.job}</span>
                  <span className="ml-auto text-xs uppercase tracking-[0.16em] text-white/50" data-id="element-316" data-mp="19ssj3s6pxych">
                    {review.source}
                  </span>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>

          <div className="mt-5 flex items-center justify-between gap-4" data-id="element-317" data-mp="36oq8zlyy04aj">
            <div className="flex items-center gap-2" role="tablist" aria-label="Choose a review" data-id="element-318" data-mp="385dj7fxyye9o">
              {reviews.map((item, i) => <button key={item.id} type="button" role="tab" aria-selected={i === index} aria-label={`Review from ${item.author}`} onClick={() => {
              setDirection(i > index ? 1 : -1);
              setIndex(i);
            }} className={`h-2.5 rounded-full transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-500 ${i === index ? 'w-7 bg-ember-500' : 'w-2.5 bg-white/30 hover:bg-white/50'}`} data-id="element-319" data-mp="3d1u8wekth0ov" data-mpi={i} />)}
            </div>

            <div className="flex items-center gap-2" data-id="element-320" data-mp="26n39kfbcdnwy">
              {[{
              label: 'Previous review',
              icon: ChevronLeftIcon,
              step: -1
            }, {
              label: 'Next review',
              icon: ChevronRightIcon,
              step: 1
            }].map(({
              label,
              icon: Icon,
              step
            }) => <button key={label} type="button" aria-label={label} onClick={() => go(step)} className="flex h-11 w-11 items-center justify-center rounded-md border border-white/25 text-white transition-colors duration-150 ease-out hover:border-ember-500 hover:bg-ember-500 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-500" data-id="element-321" data-mp="orb3dufnh24t">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-322" data-mp="zvwrpgubynr" />
                </button>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}